import React, { useState } from "react";
import { useTranslation } from "../../../contexts/I18nProvider";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { cn, formatLanguageName } from "../../../lib/utils";
import { AudioLines } from "lucide-react";
import { colors, MAX_SENTENCES } from "../../../lib/constants";
import ParagraphCanvas from "./paragraph-canvas";
import { SentenceDropdown } from "./sentence-dropdown";
import { usePolyglotContext } from "../polyglot-context";
import { useDispatch, useSelector } from "react-redux";
import {
  selectAfricanOnly,
  selectCurrentPage,
  selectCurrentChapter,
  selectItemsPerPage,
} from "../../../redux/selectors/filterSelectors";
import { selectHasPaid } from "../../../redux/selectors/hasPaidSelectors";
import {
  selectCurrentLanguage,
  selectPurchasedLanguages,
} from "../../../redux/selectors/languageSelectors";
import {
  selectAudioProgress,
  selectCurrentSentences,
  selectIsHighlighted,
  selectSentencesLoading,
} from "../../../redux/selectors/sentenceSelectors";
import {
  selectBooksLoading,
  selectCurrentBook,
} from "../../../redux/selectors/bookSelectors";
import { toggleHighlight } from "../../../redux/slices/sentenceSlice";
import { Skeleton } from "../../../components/ui/skeleton";
import generateLucideClass from "../_services/generateIconName";

function SentenceCard(props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { idx, language } = props;

  const dispatchHandleHighlight = () => dispatch(toggleHighlight(language));
  const isHighlighted = useSelector(selectIsHighlighted(language));

  const hasPaid = useSelector(selectHasPaid);
  const purchasedLanguageIds = useSelector(selectPurchasedLanguages).map(
    (item) => item.id
  );
  const africanOnly = useSelector(selectAfricanOnly);
  const itemsPerPage = useSelector(selectItemsPerPage);
  const currentPage = useSelector(selectCurrentPage);
  const audioProgress = useSelector(selectAudioProgress);
  const sentenceLoading = useSelector(selectSentencesLoading);
  const booksLoading = useSelector(selectBooksLoading);

  const currentLangauge = useSelector(selectCurrentLanguage(language));
  const currentBook = useSelector(selectCurrentBook(currentLangauge?.id));
  const currentChapter = useSelector(selectCurrentChapter(currentBook?.id));

  const currentSentences = useSelector(
    selectCurrentSentences(
      currentBook?.id,
      currentChapter?.chapter_id,
      itemsPerPage
    )
  );
  const hasAudioInCard = currentSentences?.some(
    (sentence) => sentence.audio_path
  );

  const isPurchasedLanguage = currentLangauge
    ? purchasedLanguageIds.includes(currentLangauge.id)
    : false;
  const hasAnyPurchased = purchasedLanguageIds.length > 0;
  const canAccessFullLanguage =
    hasPaid && (!hasAnyPurchased || isPurchasedLanguage);
  const overFreeLimit = currentPage * itemsPerPage > MAX_SENTENCES;
  const canPlayAudio =
    !hasPaid || canAccessFullLanguage || (!canAccessFullLanguage && !overFreeLimit);
  const shouldLockCard =
    hasPaid && hasAnyPurchased && !isPurchasedLanguage && overFreeLimit;
  const shouldShowLockedBadge =
    overFreeLimit && (!hasPaid || !canAccessFullLanguage);

  const row = Math.floor(idx / 3);
  const colorIndex = (idx + row) % colors.length;

  const [isAudioPlaying, setIsAudioPlaying] = useState(false);

  const { playAudio, audioRef } = usePolyglotContext() || {};

  const handlePlayAudio = (audio_path) => {
    if (!playAudio || !audioRef?.current) {
      console.error("Audio context or ref not available");
      return;
    }

    // Define event handlers
    const handleAudioEnded = () => {
      setTimeout(() => {
        setIsAudioPlaying(false);
      }, 300);
    };

    const handleAudioError = (e) => {
      console.error("Audio playback error:", e);
      setTimeout(() => {
        setIsAudioPlaying(false);
      }, 300);
    };

    // Remove existing listeners to prevent duplicates
    audioRef.current.removeEventListener("ended", handleAudioEnded);
    audioRef.current.removeEventListener("error", handleAudioError);

    // Add new listeners
    audioRef.current.addEventListener("ended", handleAudioEnded);
    audioRef.current.addEventListener("error", handleAudioError);

    // Play the audio
    setIsAudioPlaying(true);
    playAudio(audio_path);
  };

  return (
    <Card
      className={cn(
        "polyglot-card overflow-y-auto transition-all duration-300 border border-border/40 ease-in h-full rounded-2xl shadow-sm",
        isHighlighted ? "bg-white" : "bg-white/95 lg:bg-white/80 hover:bg-white"
      )}
      style={{
        userSelect: "none",
        borderColor: colors[colorIndex].text + "6a",
      }}
    >
      <CardHeader className="space-y-1 pb-2 shadow-sm border-b  border-white relative overflow-hidden">
        <CardTitle className="text-xl flex gap-2 justify-between items-center">
          {/* Progress Bar */}
          {hasAudioInCard && (
            <div className="absolute bottom-0 left-0 w-12 h-[2px] rounded-xl bg-transparent  mt-2 overflow-hidden">
              {isAudioPlaying && (
                <div
                  className="h-full transition-all duration-200 bg-primary"
                  style={{ width: `${audioProgress}%` }}
                />
              )}
            </div>
          )}
          {/* LanguageIcon */}
          <div
            className={cn(
              generateLucideClass(currentLangauge?.icon, currentLangauge?.id),
              "absolute z-10 translate-x-[-50%] text-gray-100 font-light translate-y-[-50%] transition-all ease-in duration-150",
              isHighlighted
                ? "opacity-0 left-20 top-10 text-[0px]"
                : "left-9 top-10  opacity-100 text-xs"
            )}
          ></div>
          {/* Background Overlay */}
          <div
            className={cn(
              "h-6 w-6  rounded-full grid place-items-center cursor-pointer absolute top-7 left-6 transition-all duration-700 ease-in-out z-0",
              isHighlighted ? `left-[-10px] scale-[3400%]` : "scale-100 "
            )}
            id="backgroundOverlay"
            onClick={() => dispatchHandleHighlight(currentBook?.id + idx)}
            style={{
              background: colors[colorIndex].bg,
            }}
          ></div>

          {/* Card Title */}
          <div
            className={cn(
              "z-10 h-[28px] flex-1 cursor-pointer transition-all duration-100 ease-in-out flex gap-1 items-center font-medium",
              isHighlighted ? "ml-0 text-sm" : "ml-8 text-md  "
            )}
            onClick={() => dispatchHandleHighlight(currentBook?.id + idx)}
            style={{
              color: isHighlighted
                ? colors[colorIndex].text
                : "hsl(var(--foreground))",
            }}
          >
            {formatLanguageName(currentLangauge?.name) || (
              <Skeleton className="h-7 rounded-xl w-44 border border-primary/20 bg-primary/10"></Skeleton>
            )}
            {/* LanguageIcon */}
            <div
              className={cn(
                generateLucideClass(currentLangauge?.icon, currentLangauge?.id),
                "-ml-1 text-gray-100 font-light transition-all ease-in duration-150",
                !isHighlighted
                  ? "opacity-0 text-[0px]"
                  : "left-9 top-10  opacity-100 text-xl"
              )}
            ></div>
          </div>
          <SentenceDropdown
            isHighlighted={isHighlighted}
            title={formatLanguageName(currentLangauge?.name)}
            book={currentBook}
            playAudio={
              hasAudioInCard && (!overFreeLimit || canAccessFullLanguage)
                ? () =>
                    currentSentences.forEach((item) =>
                      playAudio(item.audio_path)
                    )
                : null
            }
          />
        </CardTitle>
      </CardHeader>
      <CardContent className="grid gap-1 pt-2 w-full relative">
        <div
          className={cn(
            "polyglot-card-body grid gap-1",
            shouldLockCard ? "opacity-100 pointer-events-none" : ""
          )}
        >
          {/* Audio Icon */}
          {hasAudioInCard && (
            <AudioLines
              style={{
                stroke: colors[colorIndex].bg,
              }}
              className={cn(
                "absolute size-8 opacity-[14%] text-black bottom-0 -right-5"
              )}
            />
          )}
          {currentSentences?.length > 0 &&
            currentSentences.map((sentence) => (
              <SentenceItem
                key={sentence.id}
                sentence={sentence}
                hasPaid={hasPaid}
                africanOnly={africanOnly}
                canAccessFullLanguage={canAccessFullLanguage}
                canPlayAudio={canPlayAudio}
                handlePlayAudio={handlePlayAudio}
                colorIndex={colorIndex}
              />
            ))}
          {hasAudioInCard && (
            <div className="flex items-center gap-2 text-emerald-600 text-sm font-semibold">
              <AudioLines className="size-4" />
              <span>Audio Available</span>
            </div>
          )}

          {sentenceLoading && booksLoading && (
            <LoadingSkeleton
              count={itemsPerPage - (currentSentences?.length || 0)}
            />
          )}

          {currentSentences?.length === 0 &&
          !booksLoading &&
          !sentenceLoading ? (
            <NoSentencesMessage t={t} />
          ) : (
            <LoadingSkeleton
              count={itemsPerPage - (currentSentences?.length || 0)}
            />
          )}
        </div>
        {shouldLockCard && (
          <div className="pointer-events-none absolute inset-0 rounded-md bg-gradient-to-b from-transparent via-white/85 to-white"></div>
        )}
        {shouldShowLockedBadge && (
          <div className="pointer-events-none absolute top-3 right-3 rounded-full bg-amber-200 px-2 py-1 text-[11px] font-semibold text-amber-900 shadow">
            {t('polyglot.lockedAfterFreeLimit')}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default SentenceCard;
const SentenceItem = ({
  sentence,
  hasPaid,
  africanOnly,
  canAccessFullLanguage,
  canPlayAudio,
  maxVisibleWords,
  handlePlayAudio,
  colorIndex,
}) => {
  const shouldBeVisible = hasPaid
    ? true
    : sentence.sentence_counter < MAX_SENTENCES;
  const shouldTruncateSentence =
    sentence.sentence_counter > MAX_SENTENCES &&
    (!hasPaid || !canAccessFullLanguage);
  const shouldDisableAudio =
    sentence.sentence_counter > MAX_SENTENCES && !canAccessFullLanguage;

  return (
    <ParagraphCanvas
      sentence={sentence}
      africanOnly={africanOnly}
      shouldBeVisible={shouldBeVisible}
      canPlayAudio={canPlayAudio && !shouldDisableAudio}
      maxVisibleWords={shouldTruncateSentence ? 2 : null}
      playAudio={() => handlePlayAudio(sentence.audio_path)}
      MAX_SENTENCES={MAX_SENTENCES}
      colorIndex={colorIndex}
    />
  );
};

const LoadingSkeleton = ({ count }) => (
  <div className="flex flex-col gap-4 mt-2 tracking-wide leading-6">
    {Array.from({ length: count }, (_, k) => (
      <div key={k} className="flex flex-col gap-2">
        <div className="flex gap-2">
          <Skeleton className="size-4 bg-primary/10 border border-primary/20 rounded-full" />
          <Skeleton className="h-4 w-48 bg-primary/10 border border-primary/20 rounded-xl" />
        </div>
        <Skeleton className="h-4 w-64 bg-primary/10 border border-primary/20 rounded-xl" />
      </div>
    ))}
  </div>
);

const NoSentencesMessage = ({ t }) => (
  <div
    style={{
      fontSize: "18px",
      color: "#777",
      userSelect: "none",
    }}
    className="flex flex-col gap-4 mt-2 tracking-wide leading-6"
  >
    {t('polyglot.noSentences')}
  </div>
);
